import BENEFICIARY_SUBJECT_TYPE_KEY from '@Domain/Subjects/BeneficiarySubjectTypeKey';
import buildSubjectListPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectListPath';
import FetchBeneficiaryIndicators from '@Application/Indicators/Queries/FetchBeneficiaryIndicators';
import buildIndicatorQueryKey from '@Application/Indicators/buildIndicatorQueryKey';
import fetchBeneficiaryIndicators from '@Application/Indicators/fetchBeneficiaryIndicators';
import { theme } from '@Guidelines/Theme/theme';
import { css } from '@emotion/react';
import VisuallyHiddenText from '@UI/Pages/Home/Shared/VisuallyHiddenText';
import IndicatorBlockState from '@UI/Pages/Home/Shared/IndicatorBlockState';
import IndicatorCard from '@UI/Pages/Home/Shared/IndicatorCard';
import buildNewBeneficiariesText from '@UI/Pages/Home/BeneficiariesBlock/Helpers/buildNewBeneficiariesText';
import resolveBeneficiaryListScope from '@UI/Pages/Home/Helpers/resolveBeneficiaryListScope';
import formatCount from '@UI/Pages/Home/Helpers/formatCount';
import formatIndicatorPeriod from '@UI/Pages/Home/Helpers/formatIndicatorPeriod';
import useHomeIndicatorScope from '@UI/Pages/Home/Hooks/useHomeIndicatorScope';
import useRetainedIndicatorView from '@UI/Pages/Home/Hooks/useRetainedIndicatorView';
import useOwnershipWording from '@UI/Pages/Home/Hooks/useOwnershipWording';
import homePeriodSignal from '@UI/Pages/Home/Signals/homePeriodSignal';
import BeneficiaryFilterView from '@UI/Pages/Beneficiaries/BeneficiaryList/Filters/BeneficiaryFilterView';
import { setBeneficiaryFilters } from '@UI/Pages/Beneficiaries/BeneficiaryList/Filters/Signals/beneficiaryFiltersSignal';
import useCurrentOrganization from '@UI/Providers/CurrentOrganizationProvider/useCurrentOrganization';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSignalValue } from '@verclam/pulsar';
import { useDispatch, useStoreQuery } from '@verclam/synapse';

const figureStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.625rem;
`;

const figureRowStyle = css`
	margin: 0;
	display: flex;
	flex-wrap: wrap;
	align-items: baseline;
	gap: 0.625rem;
`;

const bigNumberStyle = css`
	font-family: ${theme.fonts.display};
	font-size: 3rem;
	font-weight: ${theme.fontWeights.semibold};
	line-height: 1;
	letter-spacing: -0.04em;
	color: ${theme.tints.green.solid};

	@media (max-width: ${theme.layout.compactBreakpoint}) {
		font-size: 2.5rem;
	}
`;

const unitStyle = css`
	font-family: ${theme.fonts.body};
	font-size: 1rem;
	font-weight: ${theme.fontWeights.medium};
	line-height: 1.3;
	color: ${theme.colors.ink};
`;

const newLineStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: 0.84375rem;
	font-weight: ${theme.fontWeights.medium};
	line-height: 1.5;
	color: ${theme.colors.inkSecondary};
`;

/** Active dossiers of the scope, and how many were opened over the window. */
const BeneficiariesBlock: React.FC = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const period = useSignalValue(homePeriodSignal);
	const { status, scope, indicatorScope } = useHomeIndicatorScope();
	const { organizations } = useCurrentOrganization();
	const isOwnershipWording = useOwnershipWording();

	const queryKey = indicatorScope === null ? null : buildIndicatorQueryKey(indicatorScope, period);

	const { data, error, refetch } = useStoreQuery({
		selector: (state) => state.indicatorsStore.beneficiaries,
		stopOn: (view) => queryKey === null || (view !== null && view.queryKey === queryKey),
		query: () => {
			if (indicatorScope === null) {
				return Promise.resolve();
			}

			return dispatch(
				fetchBeneficiaryIndicators(new FetchBeneficiaryIndicators({ scope: indicatorScope, period })),
			);
		},
		deps: [queryKey],
	});

	const { view, isRefreshing } = useRetainedIndicatorView(data, queryKey);

	const handleOpenBeneficiaries = React.useCallback(() => {
		const listScope = resolveBeneficiaryListScope(scope, organizations);

		setBeneficiaryFilters(
			BeneficiaryFilterView.createDefault().setAgencyScope(listScope.scope, listScope.facilityIds),
		);
		navigate(buildSubjectListPath(BENEFICIARY_SUBJECT_TYPE_KEY));
	}, [navigate, scope, organizations]);

	const renderBody = (): React.ReactNode => {
		if (status === 'unavailable') {
			return <IndicatorBlockState kind="empty" title="Aucune agence ne vous est rattachée." />;
		}

		if (error !== null) {
			return <IndicatorBlockState kind="error" onRetry={refetch} />;
		}

		if (view === null) {
			return <IndicatorBlockState kind="loading" variant="beneficiaries" />;
		}

		if (view.active === 0) {
			const formattedPeriod = formatIndicatorPeriod(view.period);

			return (
				<IndicatorBlockState
					kind="empty"
					title="Aucun bénéficiaire actif sur ce périmètre"
					range={`${formattedPeriod.from} – ${formattedPeriod.to} (heure de Paris)`}
					actionLabel="Ouvrir les bénéficiaires"
					onAction={handleOpenBeneficiaries}
				/>
			);
		}

		const unit = view.active > 1 ? 'bénéficiaires actifs' : 'bénéficiaire actif';
		const newText = buildNewBeneficiariesText(view.created, view.period.days);

		return (
			<div css={figureStyle}>
				<p css={figureRowStyle}>
					<span css={bigNumberStyle} aria-hidden="true">
						{formatCount(view.active)}
					</span>
					<span css={unitStyle} aria-hidden="true">
						{unit}
					</span>
					<VisuallyHiddenText>{`${formatCount(view.active)} ${unit}, ${newText.spoken}.`}</VisuallyHiddenText>
				</p>
				<p css={newLineStyle} aria-hidden="true">
					{newText.visible}
				</p>
			</div>
		);
	};

	return (
		<IndicatorCard
			title={isOwnershipWording ? 'Mes bénéficiaires' : 'Bénéficiaires'}
			tooltip={`Bénéficiaires dont le dossier est ouvert ${
				isOwnershipWording ? 'et qui vous sont affectés' : 'sur le périmètre'
			}. « Nouveaux » : dossiers créés pendant la période.`}
			band={theme.home.indicatorBand.beneficiaries}
			isRefreshing={isRefreshing && error === null}
		>
			{renderBody()}
		</IndicatorCard>
	);
};

export default BeneficiariesBlock;
